/** viz の状態を文字にする（viz-protocol.md §3.1 の値の表現） */
import type { VizState } from '../../lib/viz';

/** 値 1 つを Swift に近い見た目の文字列にする */
export function formatVizValue(v: unknown): string {
  if (v === null || v === undefined) return 'nil';
  if (typeof v === 'string') return `"${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.map(formatVizValue).join(', ')}]`;
  if (typeof v === 'object') {
    if ('more' in v && Object.keys(v).length === 1) return `…（残り ${String(v.more)} 件）`;
    if ('omitted' in v) return '（大きいため省略）';
    return `(${Object.entries(v)
      .map(([k, x]) => `${k}: ${formatVizValue(x)}`)
      .join(', ')})`;
  }
  return String(v);
}

/** 配列の要素（list / stack / queue / set）。要素でないものは空 */
export function vizItems(s: VizState): unknown[] {
  return Array.isArray(s.value) ? s.value : [];
}

/** 辞書の [key, value] の列 */
export function vizEntries(s: VizState): [unknown, unknown][] {
  if (!Array.isArray(s.value)) return [];
  return s.value.flatMap((e): [unknown, unknown][] =>
    Array.isArray(e) && e.length === 2 ? [[e[0], e[1]]] : [],
  );
}

/** 読み上げ用の 1 文（「i = 2、sum = 1」） */
export function summarizeState(state: Record<string, VizState>): string {
  return Object.entries(state)
    .map(([label, s]) => {
      if (s.kind === 'value') return `${label} = ${formatVizValue(s.value)}`;
      if (s.kind === 'dict') {
        const e = vizEntries(s).map(([k, v]) => `${formatVizValue(k)}: ${formatVizValue(v)}`);
        return `${label} = [${e.join(', ')}]`;
      }
      const marks = s.marks
        ? Object.entries(s.marks)
            .map(([k, i]) => `${k} = ${i}`)
            .join('、')
        : '';
      const head = s.head !== undefined ? `、head = ${s.head}` : '';
      return `${label} = ${formatVizValue(s.value)}${head}${marks ? `、${marks}` : ''}`;
    })
    .join('、');
}

/** 前のフレームから値が変わったラベル */
export function changedLabels(
  prev: Record<string, VizState> | undefined,
  next: Record<string, VizState>,
): Set<string> {
  const out = new Set<string>();
  for (const [k, v] of Object.entries(next)) {
    if (JSON.stringify(prev?.[k]) !== JSON.stringify(v)) out.add(k);
  }
  return out;
}
